import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from "styled-components";

import debounce from 'lodash.debounce';

const Div = styled.div`
    .context-menu {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 100;
        background-color: #fff;
        border: 1px solid #ccc;
    }

    .selected-items {
        overflow-y: scroll;
        padding-top: 5px;
        padding-bottom: 5px;
    }

    .selected-items label {
    }

    .list-group-item:first-child {
        border-top-left-radius: 5px !important;
    }

    .unselected-items {
        margin-top: 5px;
        border-top: 1px solid #ccc;
        border-bottom: 1px solid #ccc;

        border-radius: 5px;
        border-color: #ccc;
        padding-bottom: -100px; //this fixes infiniteScroll
    }

    .unselected-items .list-group-item:first-child {
        border-top: unset;
    }

    .list-group {
        overflow: hidden;
    }

    .list-group-item {
        white-space: nowrap;
    }

    .facilities {
        max-height: 300px;
        overflow-y: scroll;
    }

    .facilities label,
    .sensors label {
        font-size: small;
    }

    hr {
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
        border: 0;
        border-top: 1px solid #ccc;
    }
`;

// Constants for virtualized list
const ITEM_HEIGHT = 36.4; // Approximate height of a list item
const VISIBLE_COUNT = 8; // Number of items visible at a time
const BUFFER = 15; // Buffer items above and below

const WellElements = ({
    processed_data,
    handleChange,
    selector_type,
    disabled,
}) => {
    // Container ref for scroll handling
    const scrollContainerRef = useRef(null);
    const [startIndex, setStartIndex] = useState(0);
    const [prev_process_data_length, setPrevProcessDataLength] = useState(0);

    // Create a memoized debounced scroll handler
    const debouncedScroll = useCallback(
        debounce((scrollTop) => {
            const index = Math.floor(scrollTop / ITEM_HEIGHT);
            setStartIndex(index);
        }, 100),
        []
    );
    
    // Scroll handler that calls the debounced function
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;
        const scrollTop = scrollContainerRef.current.scrollTop;
        debouncedScroll(scrollTop);
    }, [debouncedScroll]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
        }
        return () => {
            scrollContainer?.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    useEffect(() => {
        if (processed_data.length !== prev_process_data_length) {
            setStartIndex(0);
            setPrevProcessDataLength(processed_data.length);
            // Reset scroll position when data changes
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
        }
    }, [processed_data, prev_process_data_length]);

    const selected_data = processed_data.filter(
        (facility) => facility.is_selected
    );

    // Calculate which items should be visible
    const unselectedData = processed_data.filter(item => {
        return selector_type !== "checkbox" || !item.is_selected;
    });

    const visibleStartIndex = Math.max(startIndex - BUFFER, 0);
    const visibleEndIndex = Math.min(startIndex + VISIBLE_COUNT + BUFFER, unselectedData.length);
    const visibleItems = unselectedData.slice(visibleStartIndex, visibleEndIndex);

    return (
        <Div>
            {selector_type === "checkbox" && (
                <div>
                    <ul className="list-group facilities selected-items">
                        {selected_data.map((facility) => {
                            return (
                                <WellElement
                                    key={facility.p2_merrick_id}
                                    facility={facility}
                                    handleChange={handleChange}
                                    selector_type={selector_type}
                                    disabled={disabled}
                                />
                            );
                        })}
                    </ul>
                </div>
            )}
            <div 
                ref={scrollContainerRef}
                className="list-group facilities unselected-items"
                style={{ 
                    height: `${VISIBLE_COUNT * ITEM_HEIGHT}px`,
                    overflowY: 'auto'
                }}
            >
                <div
                    style={{
                        height: `${unselectedData.length * ITEM_HEIGHT}px`,
                        position: 'relative'
                    }}
                >
                    <div
                        style={{
                            paddingTop: `${visibleStartIndex * ITEM_HEIGHT}px`,
                            paddingBottom: `${(unselectedData.length - visibleEndIndex) * ITEM_HEIGHT}px`
                        }}
                    >
                        {visibleItems.map((facility) => {
                            return (
                                <WellElement
                                    key={facility.p2_merrick_id}
                                    facility={facility}
                                    handleChange={handleChange}
                                    selector_type={selector_type}
                                    disabled={disabled}
                                    // NOTE: ideally item height should be passed as a prop
                                    // but for now we are letting word-wrap to take care of it
                                    // this way, scrollbar will readjust sometimes after scrolling
                                    // Uncomment the line below to set a fixed height
                                    // style={{ height: `${ITEM_HEIGHT}px` }}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </Div>
    );
};

export default WellElements;

const Div2 = styled.div`
    label {
        display: flex;
        flex-wrap: nowrap;
        justify-content: space-between;
        align-items: center;
    }

    label > div {
        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: auto;
        white-space: normal;
    }

    label > i {
        flex-shrink: 0;
    }

    .fa-ellipsis {
        padding: 0.25rem 0.25rem;
    }

    .fa-ellipsis:hover {
        cursor: pointer;
        background-color: #e9ecef;
        border-radius: 0.25rem;
    }
`;

const WellElement = ({
    facility,
    handleChange,
    handleContextMenu,
    selector_type,
    disabled,
    hidden,
    style,
}) => {
    const final_style = {
        ...style,
        display: hidden ? "none" : "block",
    }

    return (
        <Div2
            className="list-group-item"
            style={final_style}
        >
            <label
                id={facility.p2_merrick_id}
                className={
                    "justify-content-between " +
                    (hidden ? "" : " d-flex")
                }
            >
                <div className="me-2">
                    <input
                        className="form-check-input me-1"
                        checked={facility.is_selected?facility.is_selected:false}
                        name="facility"
                        type={selector_type}
                        onChange={(e) => handleChange(facility.p2_merrick_id)}
                        disabled={facility.is_disabled || disabled}
                    />
                    {/* display facility.p2_well_name in gray if disabled */}
                    <span
                        className={
                            facility.is_disabled || disabled ? "text-muted" : ""
                        }
                    >
                        {facility.p2_well_name}
                        {facility.is_cached && (
                            <i
                                className="fas fa-check ms-1"
                                style={{ color: "green" }}
                            ></i>
                        )}
                    </span>
                </div>

                {handleContextMenu && (
                <i
                    className="fas fa-ellipsis"
                    onClick={handleContextMenu}
                ></i>
                )}
            </label>
        </Div2>
    );
};
